import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse, AxiosError } from 'axios';
import { Toast } from 'react-vant';
import i18n from '../i18n';
// 定义响应数据的接口
interface ApiResponse<T = any> {
    code: number;
    data: T;
    message?: string;
}

// 创建 axios 实例
const request: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL_V1 || '', // 使用正确的环境变量名称
    timeout: 30000, // 30秒超时
    headers: {
        'Content-Type': 'application/json',
        'Channel-Code': 'IkLNor0h6wg',
    },
});

// 请求拦截器
request.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // 添加 Token 到请求头
        const token = localStorage.getItem('token');
        if (token && config.headers) {
            config.headers.Token = token;
        }
        
        // 如果是 FormData，删除 Content-Type 让浏览器自动设置（包含 boundary）
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        
        return config;
    },
    (error: AxiosError) => {
        console.error('请求错误:', error);
        return Promise.reject(error);
    }
);

// 响应拦截器
request.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
        const { data } = response;

        // 检查业务状态码
        if (data.code === 0) {
            // 1004 在现有逻辑上需要因为需要根据这个有没有卡来判断是否开卡
            // 成功响应，返回数据
            return data.data;
        } else if (data.code === 1004) {
            // 没有卡片
            return data;
        } else if (data.code === 1001) {
            Toast.info({
                message: i18n.t('登录过期请重新登录'),
                duration: 1000,
                onClose: () => {
                    // 跳转到登录页面
                    window.location.href = '/login';
                    localStorage.removeItem('token');
                    localStorage.removeItem('app_state');
                }
            })
            return Promise.reject(new Error('Token Error'));
        } else {
            Toast.fail({
                position: 'middle',
                message: data.message || `errorCode: ${data.code}`,
            })
            // 其他 code 码，打印错误信息
            console.error(`API错误 [${data.code}]:`, data.message || '未知错误');
            return Promise.reject(new Error(data.message || `${data.code}`));
        }
    },
    (error: AxiosError) => {
        // 如果是取消请求，静默处理，不显示错误提示
        if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
            return Promise.reject(error);
        }

        // 处理网络错误、超时等
        if (error.code === 'ECONNABORTED') {
            // 请求超时
            Toast.fail({
                position: 'middle',
                message: i18n.t('请求超时请稍后重试'),
            })
            console.error('请求超时');
        } else if (error.response) {
            // 服务器响应了错误状态码
            const { data } = error.response as AxiosResponse<ApiResponse>;
            Toast.fail({
                position: 'middle',
                message: data.message! || `errorCode: ${data.code!}`,
            })
        } else if (error.request) {
            // 请求已发出但没有收到响应
            console.error('网络错误，请检查网络连接');
            console.log('error.request', error.request)
            Toast.fail({
                position: 'middle',
                message: i18n.t('网络环境异常请稍后重试'),
            })
            throw new Error(error.request);
        } else {
            // 其他错误
            console.error('请求配置错误:', error.message);
        }

        return Promise.reject(error);
    }
);

// 封装常用的请求方法
export const api = {
    get: <T = any>(url: string, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.get(url, config);
    },

    post: <T = any>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.post(url, data, config);
    },

    put: <T = any>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.put(url, data, config);
    },

    delete: <T = any>(url: string, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.delete(url, config);
    },

    patch: <T = any>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.patch(url, data, config);
    }
};

// 导出 axios 实例（如果需要更复杂的配置）
export default request;

// 导出类型定义
export type { ApiResponse, InternalAxiosRequestConfig, AxiosResponse };

/**
 * 创建可取消的请求
 * 返回一个对象，包含 promise 和 cancel 方法
 */
export interface CancellableRequest<T> {
    promise: Promise<T>;
    cancel: () => void;
}

/**
 * 创建可取消的 POST 请求
 * @param url 请求地址
 * @param data 请求数据
 * @returns CancellableRequest 对象
 */
export function createCancellablePost<T = any>(url: string, data?: any): CancellableRequest<T> {
    const controller = new AbortController();
    
    const promise = request.post<any, T>(url, data, {
        signal: controller.signal
    } as any);

    return {
        promise,
        cancel: () => controller.abort()
    };
}

/**
 * 创建可取消的 GET 请求
 * @param url 请求地址
 * @param config 请求配置
 * @returns CancellableRequest 对象
 */
export function createCancellableGet<T = any>(url: string, config?: any): CancellableRequest<T> {
    const controller = new AbortController();
    
    const promise = request.get<any, T>(url, {
        ...config,
        signal: controller.signal
    });

    return {
        promise,
        cancel: () => controller.abort()
    };
}

/**
 * 判断错误是否为取消请求导致的
 */
export function isRequestCancelled(error: any): boolean {
    return axios.isCancel(error) || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
}